import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, ErrorState, PageHeader } from '@/components/page';
import { Pagination } from '@/components/pagination';
import { CaseStatusBadge, EntryTypeBadge } from '@/features/register/badges';
import { FilterSelect } from '@/features/register/filter-select';
import { api } from '@/lib/api';
import { can, useMe } from '@/lib/auth';
import { formatDate, SOURCE_LABEL } from '@/lib/labels';
import type { CaseSource, CaseStatus, FraudCase, Page } from '@/lib/types';

interface CasesSearch {
  ref?: string;
  source?: CaseSource;
  status?: CaseStatus;
  page?: number;
}

export const Route = createFileRoute('/_app/cases/')({
  validateSearch: (s: Record<string, unknown>): CasesSearch => ({
    ref: typeof s.ref === 'string' && s.ref.trim() ? s.ref : undefined,
    source: ['APPLICATION', 'MANUAL', 'BACKFILL'].includes(s.source as string) ? (s.source as CaseSource) : undefined,
    status: ['ACTIVE', 'REVERSED'].includes(s.status as string) ? (s.status as CaseStatus) : undefined,
    page: Number(s.page) > 1 ? Math.floor(Number(s.page)) : undefined,
  }),
  component: CasesPage,
});

const PAGE_SIZE = 25;

function CasesPage() {
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [ref, setRef] = useState(search.ref ?? '');
  useEffect(() => setRef(search.ref ?? ''), [search.ref]);

  const query = useQuery({
    queryKey: ['cases', search],
    queryFn: () =>
      api.get<Page<FraudCase>>('/v1/cases', {
        sourceReference: search.ref,
        source: search.source,
        status: search.status,
        page: search.page ?? 1,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const set = (patch: Partial<CasesSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: 'page' in patch ? patch.page : undefined }) });

  function submit(e: FormEvent) {
    e.preventDefault();
    set({ ref: ref.trim() || undefined });
  }

  return (
    <>
      <PageHeader
        title="Cases"
        description="Each case is one report of fraud: from an application, a manual entry, or the historical backfill."
        actions={
          can.record(me) && can.write(me) ? (
            <Button asChild>
              <Link to="/cases/new">
                <Plus /> {can.admin(me) ? 'New entry' : 'Record fraud'}
              </Link>
            </Button>
          ) : null
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form onSubmit={submit} className="flex min-w-[240px] gap-2">
          <label htmlFor="cases-ref" className="sr-only">
            Application reference
          </label>
          <Input id="cases-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Application reference" className="h-9 w-56" />
          <Button type="submit" variant="outline" size="sm" className="h-9">
            Find
          </Button>
        </form>
        <FilterSelect
          label="Source"
          value={search.source}
          options={(Object.keys(SOURCE_LABEL) as CaseSource[]).map((s) => ({ value: s, label: SOURCE_LABEL[s] }))}
          onChange={(source) => set({ source })}
        />
        <FilterSelect
          label="Status"
          value={search.status}
          options={[
            { value: 'ACTIVE', label: 'Active' },
            { value: 'REVERSED', label: 'Reversed' },
          ]}
          onChange={(status) => set({ status })}
        />
      </div>

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted hover:bg-muted">
                <TableHead className="px-4">Case</TableHead>
                <TableHead>Entry type</TableHead>
                <TableHead className="text-right">Details</TableHead>
                <TableHead>Reported by</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reported</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isPending
                ? Array.from({ length: 6 }, (_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={6} className="px-4">
                        <Skeleton className="h-9 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                : query.data.items.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="px-4 py-2.5">
                        <Link to="/cases/$caseId" params={{ caseId: c.id }} className="font-medium text-link underline-offset-4 hover:underline">
                          {c.source === 'APPLICATION' ? c.sourceReference : c.source === 'MANUAL' ? 'Manual entry' : (c.sourceReference ?? 'Backfill')}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          {SOURCE_LABEL[c.source]}
                          {c.brand ? ` · ${c.brand}` : ''}
                          {c.branch ? ` · ${c.branch}` : ''}
                        </div>
                      </TableCell>
                      <TableCell>
                        <EntryTypeBadge type={c.entryType} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{c.entries.length}</TableCell>
                      <TableCell className="text-muted-foreground">{c.reportedByName ?? c.reportedById}</TableCell>
                      <TableCell>
                        <CaseStatusBadge status={c.status} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(c.reportedAt)}</TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
          {query.data && query.data.items.length === 0 ? (
            <div className="p-4">
              <EmptyState title="No cases found">Cases appear here when fraud is recorded, added manually or imported.</EmptyState>
            </div>
          ) : null}
          {query.data ? <Pagination page={query.data.page} pageSize={PAGE_SIZE} total={query.data.total} onPage={(page) => set({ page })} /> : null}
        </div>
      )}
    </>
  );
}
